import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import Card from '../../../shared/components/Card';
import { getApiBaseUrl } from '../../../core/config/api';

interface Backup {
  filename: string;
  path: string;
  size: number;
  sizeFormatted: string;
  created: Date | string;
  modified: Date | string;
  userId: number;
  username: string;
}

interface User {
  id: number;
  username: string;
}

const BackupManagement: React.FC = () => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  
  // Vérifier l'accès autorisé (rôles 1, 2, 3, 4)
  const hasAccess = currentUser?.roles_id && [1, 2, 3, 4].includes(currentUser.roles_id);
  
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searching, setSearching] = useState<boolean>(false);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loadingBackups, setLoadingBackups] = useState<boolean>(false);
  const [backupsDir, setBackupsDir] = useState<string>('');
  const [selectedBackup, setSelectedBackup] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ filename: string; userId: number } | null>(null);
  const [activeTab, setActiveTab] = useState<'complete' | 'user'>('complete');
  
  // Rechercher un joueur par username
  const searchUsers = useCallback(async (term: string) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const apiUrl = getApiBaseUrl(true); // includeApiPath = true
      const response = await fetch(`${apiUrl}/users/with-possessions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const termLower = term.toLowerCase();
          const filtered = (data.data as any[])
            .filter((user: any) => 
              user.username.toLowerCase().includes(termLower) ||
              user.id.toString().includes(term)
            )
            .map((user: any) => ({
              id: user.id,
              username: user.username
            }))
            .slice(0, 10); // Limiter à 10 résultats
          setSearchResults(filtered);
        }
      }
    } catch (err: any) {
      console.error('Erreur lors de la recherche d\'utilisateurs:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);
  
  // Recherche avec debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        searchUsers(searchTerm);
      } else {
        setSearchResults([]);
        setSelectedUser(null);
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchUsers]);

  // Charger les sauvegardes d'un joueur
  const loadUserBackups = useCallback(async (userId: number) => {
    setLoadingBackups(true);
    setBackups([]);
    setBackupsDir('');
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const apiUrl = getApiBaseUrl(true);
      const response = await fetch(`${apiUrl}/backups/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setBackups(data.backups || []);
          setBackupsDir(data.directory || '');
        } else {
          setAlert({ type: 'error', message: data.error || 'Erreur lors du chargement des sauvegardes' });
        }
      } else {
        const errorData = await response.json();
        setAlert({ type: 'error', message: errorData.error || 'Erreur lors du chargement des sauvegardes' });
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des sauvegardes:', err);
      setAlert({ type: 'error', message: 'Erreur lors du chargement des sauvegardes' });
    } finally {
      setLoadingBackups(false);
    }
  }, []);

  // Charger les sauvegardes quand un joueur est sélectionné
  useEffect(() => {
    if (selectedUser && activeTab === 'user') {
      loadUserBackups(selectedUser.id);
    } else {
      setBackups([]);
      setBackupsDir('');
      setSelectedBackup(null);
    }
  }, [selectedUser, activeTab, loadUserBackups]);

  // Supprimer une sauvegarde
  const deleteBackup = useCallback(async (userId: number, filename: string) => {
    setDeleteConfirm({ filename, userId });
  }, []);

  // Confirmer et exécuter la suppression
  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm) return;

    const { userId, filename } = deleteConfirm;
    setDeleteConfirm(null);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const apiUrl = getApiBaseUrl(true);
      const response = await fetch(`${apiUrl}/backups/user/${userId}/file/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAlert({ type: 'success', message: 'Sauvegarde supprimée avec succès' });
          // Recharger les sauvegardes
          if (selectedUser) {
            loadUserBackups(selectedUser.id);
          }
          setSelectedBackup(null);
        } else {
          setAlert({ type: 'error', message: data.error || 'Erreur lors de la suppression' });
        }
      } else {
        const errorData = await response.json();
        setAlert({ type: 'error', message: errorData.error || 'Erreur lors de la suppression' });
      }
    } catch (err: any) {
      console.error('Erreur lors de la suppression:', err);
      setAlert({ type: 'error', message: 'Erreur lors de la suppression' });
    }
  }, [selectedUser, loadUserBackups, deleteConfirm]);

  // Télécharger une sauvegarde
  const downloadBackup = useCallback(async (userId: number, filename: string) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const apiUrl = getApiBaseUrl(true);
      const response = await fetch(`${apiUrl}/backups/user/${userId}/file/${encodeURIComponent(filename)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          // Créer un blob et télécharger le fichier
          const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setAlert({ type: 'success', message: 'Sauvegarde téléchargée avec succès' });
        }
      } else {
        const errorData = await response.json();
        setAlert({ type: 'error', message: errorData.error || 'Erreur lors du téléchargement' });
      }
    } catch (err: any) {
      console.error('Erreur lors du téléchargement:', err);
      setAlert({ type: 'error', message: 'Erreur lors du téléchargement' });
    }
  }, []);

  // Fermer l'alerte après 5 secondes
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);
  
  if (!hasAccess) {
    return (
      <div className="min-h-screen relative overflow-hidden">
        <BackgroundImage />
        <ParticlesBackground />
        <Navbar />
        <div className="relative z-10 pt-24 pb-16 flex items-center justify-center">
          <Card className="p-8 text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Accès Refusé</h1>
            <p className="text-gray-600">Vous n'avez pas les permissions nécessaires pour accéder à cette page.</p>
          </Card>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen relative overflow-hidden">
      <BackgroundImage />
      <ParticlesBackground />
      <Navbar />
      <div className="relative z-10 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold text-white mb-2 text-gradient">
                Gestion des Sauvegardes
              </h1>
              <p className="text-gray-300">
                Créez, restaurez et gérez les sauvegardes de la base de données
              </p>
              {backupsDir && (
                <p className="text-sm text-gray-400 mt-2">Dossier de sauvegarde : {backupsDir}</p>
              )}
            </div>
            <button
              onClick={() => navigate('/panel')}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Retour au panel</span>
            </button>
          </div>
          
          {/* Barre de recherche de joueur */}
          {activeTab === 'user' && (
            <Card className="p-6 mb-6">
              <div className="relative">
                <label className="block text-sm font-medium text-white mb-2">
                  Rechercher un joueur par pseudo ou ID
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Tapez le pseudo ou l'ID du joueur..."
                    className="w-full px-4 py-3 pl-10 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg 
                    className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {searching && (
                    <div className="absolute right-3 top-3.5">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-400"></div>
                    </div>
                  )}
                </div>
                
                {/* Résultats de la recherche */}
                {searchResults.length > 0 && !selectedUser && (
                  <div className="mt-2 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {searchResults.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => {
                          setSelectedUser(user);
                          setSearchTerm(user.username);
                          setSearchResults([]);
                        }}
                        className="px-4 py-3 hover:bg-gray-700 cursor-pointer transition-colors border-b border-gray-700 last:border-b-0"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium">{user.username}</span>
                          <span className="text-sm text-gray-400">ID: {user.id}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Joueur sélectionné */}
                {selectedUser && (
                  <div className="mt-3 p-4 bg-blue-500/20 border border-blue-500/50 rounded-lg flex items-center justify-between">
                    <div>
                      <span className="text-white font-semibold">{selectedUser.username}</span>
                      <span className="text-sm text-gray-400 ml-2">(ID: {selectedUser.id})</span>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedUser(null);
                        setSearchTerm('');
                        setSearchResults([]);
                      }}
                      className="text-gray-400 hover:text-white transition-colors"
                      title="Désélectionner"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </Card>
          )}
          
          {/* Onglets */}
          <div className="mb-6 border-b border-gray-600">
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setActiveTab('complete');
                  setSelectedUser(null);
                  setSearchTerm('');
                  setSearchResults([]);
                }}
                className={`px-4 py-2 font-semibold transition-colors ${
                  activeTab === 'complete'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
                Sauvegardes complètes
              </button>
              <button
                onClick={() => setActiveTab('user')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  activeTab === 'user'
                    ? 'text-blue-400 border-b-2 border-blue-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Sauvegardes utilisateur
                {selectedUser && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                    {selectedUser.username}
                  </span>
                )}
              </button>
            </div>
          </div>
          
          {/* Alertes */}
          {alert && (
            <div className={`mb-4 p-4 rounded-lg ${
              alert.type === 'success' 
                ? 'bg-green-500/20 border border-green-500/50 text-green-400' 
                : 'bg-red-500/20 border border-red-500/50 text-red-400'
            }`}>
              <div className="flex items-center justify-between">
                <span>{alert.message}</span>
                <button
                  onClick={() => setAlert(null)}
                  className="ml-4 text-current hover:opacity-70"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Boîte de dialogue de confirmation de suppression */}
          {deleteConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <Card className="p-6 max-w-md w-full mx-4">
                <h3 className="text-xl font-bold text-white mb-4">Confirmer la suppression</h3>
                <p className="text-gray-300 mb-6">
                  Êtes-vous sûr de vouloir supprimer la sauvegarde <strong className="text-white">{deleteConfirm.filename}</strong> ?
                  <br />
                  <span className="text-sm text-gray-400 mt-2 block">Cette action est irréversible.</span>
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Supprimer
                  </button>
                </div>
              </Card>
            </div>
          )}

          {/* Contenu selon l'onglet */}
          <Card className="p-6">
            {activeTab === 'complete' ? (
              <div className="text-center py-8">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-gray-400 text-lg mb-2">
                  Sauvegardes complètes
                </p>
                <p className="text-gray-500 text-sm">
                  Cette fonctionnalité sera disponible prochainement
                </p>
              </div>
            ) : (
              <div>
                {!selectedUser ? (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="text-gray-400 text-lg mb-2">
                      Recherchez un joueur pour voir ses sauvegardes
                    </p>
                    <p className="text-gray-500 text-sm">
                      Utilisez la barre de recherche ci-dessus pour trouver un joueur
                    </p>
                  </div>
                ) : loadingBackups ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
                    <p className="mt-4 text-gray-400">Chargement des sauvegardes...</p>
                  </div>
                ) : backups.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 mx-auto mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-gray-400 text-lg mb-2">
                      Aucune sauvegarde trouvée
                    </p>
                    <p className="text-gray-500 text-sm">
                      Ce joueur n'a pas encore de sauvegardes dans son dossier
                    </p>
                    {backupsDir && (
                      <p className="text-xs text-gray-600 mt-2">Dossier : {backupsDir}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    {backupsDir && (
                      <div className="mb-4 p-3 bg-gray-800 rounded-lg">
                        <p className="text-sm text-gray-400">
                          <span className="font-medium text-gray-300">Dossier :</span> {backupsDir}
                        </p>
                      </div>
                    )}
                    <div className="space-y-3">
                      {backups.map((backup) => (
                        <div
                          key={backup.filename}
                          className={`p-4 border-2 rounded-lg transition-all ${
                            selectedBackup === backup.filename
                              ? 'border-blue-500 bg-blue-500/20'
                              : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <input
                                  type="radio"
                                  checked={selectedBackup === backup.filename}
                                  onChange={() => setSelectedBackup(backup.filename)}
                                  className="w-4 h-4 text-blue-600"
                                />
                                <h3 className="font-semibold text-white">{backup.filename}</h3>
                              </div>
                              <div className="ml-7 flex flex-wrap gap-4 text-sm text-gray-400">
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                                  </svg>
                                  {backup.sizeFormatted}
                                </span>
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  Créé : {new Date(backup.created).toLocaleString('fr-FR')}
                                </span>
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  Modifié : {new Date(backup.modified).toLocaleString('fr-FR')}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => downloadBackup(backup.userId, backup.filename)}
                                className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded transition-colors"
                                title="Télécharger"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </button>
                              <button
                                onClick={() => deleteBackup(backup.userId, backup.filename)}
                                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                                title="Supprimer"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BackupManagement;

